import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { 
  Order, 
  MedicalConsultation,
  MedicalTest, 
  PermissionId, 
  User, 
  Role, 
  RoleName,
  Drug,
  QuantityDetails
} from '../../models';
import { 
  MedicalTestService,
  UserProfileService,
  AuthenticationService,
  OrderService,
  DataService,
  RoleService,
  PharmacyService
} from '../../services';
import Swal from 'sweetalert2';
import { first, map, mergeMap } from 'rxjs/operators';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss']
})
export class AdminComponent implements OnInit {
  users: User[] = [];
  drugs: Drug[] = [];
  isDrugs: boolean;
  isUsers: boolean = true;
  isMedicalTests: boolean;
  isOrders: boolean;
  isAllAdmins: boolean;
  loggedInUser: User;
  titles: string[] = [];
  tableData: User[] | MedicalTest[] | Order[] | Drug[];
  type: string;
  usersWithOrders: User[] = [];
  medicalTestsWithOrders: MedicalTest[] = [];
  processedTableData: User[];
  roleOptions: { label: string, value: RoleName }[] = [
    { label: 'Admin', value: RoleName.ADMIN_ONE },
    { label: 'Admin1.1', value: RoleName.ADMIN_TWO },
    { label: 'Admin1.2', value: RoleName.ADMIN_THREE },
    { label: 'Admin1.3', value: RoleName.ADMIN_FOUR }
  ];
  roles: Role[] = [];
  roleLabels: { [key: string]: string } = {};
  rolePermissions: { [key: string]: string } = {};
  isSuperAdmin: boolean;
  canViewUsers: boolean;
  canEditMedicalTests: boolean;
  canViewOrders: boolean;
  canEditOrders: boolean;
  canAddMedicalTest: boolean;
  canAddDrug: boolean;
  canEditDrugs: boolean;
  addConsultation: boolean;
  consultationAmount: number | undefined;
  tableTitle: string;
  sectionTitle: string = '';
  fullText: string | null = null;
  tooltipX: number = 0;
  tooltipY: number = 0;
  canAddDrugs: boolean;
  isAddDrug: boolean;

  constructor(
    private router: Router,
    private medicalTestService: MedicalTestService,
    private userProfileService: UserProfileService,
    private authenticationService: AuthenticationService,
    private orderService: OrderService,
    private dataService: DataService,
    private roleService: RoleService,
    private pharmacyService: PharmacyService
  ) { }

  ngOnInit(): void {
    const currentUser = this.authenticationService.currentUser();
    if (currentUser) {
      this.loggedInUser = currentUser['user'];
      this.isSuperAdmin = this.loggedInUser.role.name === 'Admin';
      const roleId = this.loggedInUser.role_id;
      this.roleService.getSingleRole(roleId).subscribe((role: Role) => {
        const permissions = role.permissions.map(p => p.id);
        this.canViewUsers = permissions.includes(PermissionId.VIEW_USERS);
        this.canEditMedicalTests = permissions.includes(PermissionId.MANAGE_MEDICAL_TESTS);
        this.canViewOrders = permissions.includes(PermissionId.MANAGE_ORDERS);
        this.canEditOrders = permissions.includes(PermissionId.EDIT_ORDER);
        this.canAddMedicalTest = permissions.includes(PermissionId.ADD_MEDICAL_TEST);
        this.getAllUsers();
        this.loadRoles();
        if (this.canViewUsers) {
          this.getAllUsers();
        } else if (this.canEditMedicalTests || this.canAddMedicalTest) {
          this.getAllLocalProducts();
        } else if (this.canViewOrders || this.canEditOrders) {
          this.getAllOrders();
        } 
      });
    }
  }

  getAllLocalProducts() {
    this.titles = this.isSuperAdmin ? 
      ['#', 'Title', 'Category', 'Test price', 'Partner price', 'Action'] : 
      ['#', 'Title', 'Category', 'Test price', 'Action'];
    this.isMedicalTests = true;
    this.isUsers = false;
    this.isOrders = false;
    this.isAllAdmins = false;
    this.isDrugs = false;
    this.medicalTestService.getMedicalTests().pipe(
      mergeMap(medicalTests => {
        this.tableData = medicalTests as MedicalTest[];
        return this.orderService.getOrders();
      })).subscribe(orders => {
        if (orders) {
          this.medicalTestsWithOrders = orders.reduce((acc, order) => {
            if (order.medical_tests) {
              order.medical_tests.map(medicalTest => {
                if (!acc.some(item => item.id === medicalTest.id)) {
                  acc.push(medicalTest);
                }
              });
            }
            return acc;
          }, [] as MedicalTest[]);
        }
    });
  }

  getAllDrugs() {
    this.titles = this.isSuperAdmin ? 
    ['#', 'Title', 'Category', 'Quantity', 'Drug price', 'Partner price', 'Stock', 'Action'] : 
    ['#', 'Title', 'Category', 'Quantity', 'Drug price', 'Stock', 'Action'];
    this.isDrugs = true;
    this.isUsers = false;
    this.isOrders = false;
    this.isAllAdmins = false;
    this.isMedicalTests = false;
    this.pharmacyService.getDrugs().pipe(first()).subscribe(response => {
      this.tableData = response as Drug[];
      const quantityTypeNames = (this.tableData as Drug[])
        .map(drug => drug.quantity_types || [])
        .reduce((acc, curr) => acc.concat(curr), [])
        .map(qty => ({ id: qty.id, name: qty.name }));
      this.dataService.saveAllLocalQuantityNames(quantityTypeNames);
    });
  }

  getAllUsers() {
    this.titles = ['#', 'First Name', 'Last Name', 'Gender', 'Location', 'Action'];
    this.isUsers = true;
    this.isMedicalTests = false;
    this.isOrders = false;
    this.isAllAdmins = false;
    this.isDrugs = false;
    this.userProfileService.getUsers().pipe(first()).subscribe(response => 
      this.tableData = response as User[]);
  }

  getAllAdmins() {
    this.titles = ['#', 'Name', 'Role', 'Edit Role', 'Permissions'];
    this.isUsers = false;
    this.isMedicalTests = false;
    this.isOrders = false;
    this.isDrugs = false;
    this.isAllAdmins = true;
    if (this.loggedInUser?.role?.name === RoleName.ADMIN_ONE) {
      this.userProfileService.getUsers().pipe(first()).subscribe(response => {
        this.tableData = (response as User[]).filter(user => {
          return [RoleName.ADMIN_ONE, RoleName.ADMIN_TWO, RoleName.ADMIN_THREE, RoleName.ADMIN_FOUR]
            .includes(user.role?.name as RoleName); 
        });
      });
    } else {
      this.tableData = [];
    }
  }  

  getAllOrders() {
    this.titles = ['#', 'Tests', 'Order No.', 'Name', 'Contact', 'Location', 'Status', 'Action'];
    this.isUsers = false;
    this.isMedicalTests = false;
    this.isOrders = true;
    this.isAllAdmins = false;
    this.isDrugs = false;
    this.orderService.getOrders().pipe(
      mergeMap(orders => {
        this.tableData = orders;
        return this.userProfileService.getUsers();
      })
    ).subscribe(users => {
        if (users) {
          this.users = users as User[];
          this.tableData.map(order => {
            const user = this.users.find(user => user.id === (order as Order).user_id);
            if (user) {
              (order as any).fullName = `${user.firstname} ${user.lastname}`;
            }
          });
        }
      });
  }

  showFullText(event: MouseEvent, text: string): void {
    this.fullText = text;
    this.tooltipX = event.clientX;
    this.tooltipY = event.clientY - 10; 
  }

  // Close tooltip when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const tooltipPopup = document.querySelector('.tooltip-popup');
    const tooltipTrigger = event.target as HTMLElement;
    // If the click is outside the tooltip and table data, close the tooltip
    if (tooltipPopup && !tooltipPopup.contains(tooltipTrigger) && !tooltipTrigger.closest('.ellipsis')) {
      this.hideFullText();
    }
  }

  hideFullText(): void {
    this.fullText = null;
  }

  onEditProduct(product: MedicalTest | Drug) {
    const isAddDrug = 'country_of_origin' in product;
    this.dataService.saveProductToEdit(product);
    this.router.navigate(['add-edit-product'], {
      state: { isAddDrug }
    });
  }

  onEditConsultationAmount() {
    this.medicalTestService.getLocalMedicalConsultationAmount().subscribe((response: MedicalConsultation) => {
      this.consultationAmount = response.amount || 0;
      this.dataService.saveLocalMedicalConsultationAmount(this.consultationAmount); 
  
      Swal.fire({
        title: `Edit Medical Consultation Amount`,
        html: `<input type="text" placeholder="${this.consultationAmount}" name="name" id="amount">`,
        showCancelButton: true,
        confirmButtonText: 'Submit',
        confirmButtonColor: 'var(--thm-base)',
        focusConfirm: false,
        customClass: 'results-alert open',
        showLoaderOnConfirm: true,
        preConfirm: () => {
          const amount = (<HTMLInputElement>Swal.getPopup()?.querySelector('#amount')).value;
          if (!amount) {
            Swal.showValidationMessage(`Amount required`);
          }
          return amount.trim();
        }
      }).then(result => {
        if (result.isConfirmed) {
          const formData = new FormData();
          formData.append('amount', result.value.toString());
          this.medicalTestService.editMedicalConsultationAmount(formData)
            .pipe(first()).subscribe(response => {
              Swal.fire({
                title: '',
                text: 'Consultation amount updated',
                icon: 'success',
                confirmButtonText: 'Ok',
                confirmButtonColor: 'var(--thm-base)',
              }).then(() => this.getAllLocalProducts());
            }, error => {
              Swal.fire({
                title: 'An error occurred',
                html: 'Please contact us at: <b>info@meshincentre.com</b> or via our contact form',
                icon: 'error',
                confirmButtonColor: '#f37b7b',
              });
              console.error('error', error);
            });
        }
      });
    });
  }

  onEditOrder(order: Order) {
    this.dataService.saveLocalOrderToEdit(order);
    this.router.navigate(['edit-order']);
  }

  onDeleteUser(user: User) {
    const protectedEmails = [
      'kather946@gmail.com',
      'ekarungi20@gmail.com',
      'miljulls@yahoo.com',
      'mirembejuliet77@gmail.com'
    ];
    const isProtected = user.email?.endsWith('@meshincentre.com') || protectedEmails.includes(user.email || '');
    if (isProtected) {
      Swal.fire({
        title: 'Action Denied',
        text: 'This user cannot be deleted.',
        icon: 'error',
        confirmButtonColor: '#f37b7b',
      });
      return;
    }
    Swal.fire({
      title: '',
      text: `Are you sure you want to delete user ${user.firstname} ${user.lastname}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete user!',
      cancelButtonColor: '#f37b7b',
      confirmButtonColor: 'var(--thm-base)',
    }).then((result) => {
      if (result.isConfirmed) {
        this.userProfileService.deleteUser(user.id as number).pipe(first()).subscribe(
          user => {
            this.isMedicalTests = false;
            Swal.fire({
              title: '',
              text: 'User deleted!',
              icon: 'success',
              confirmButtonText: 'Ok',
              confirmButtonColor: 'var(--thm-base)',
            }).then(() => this.getAllUsers());
          },
          error => {
            Swal.fire({
              title: 'An error occurred',
              html: 'Please contact us at: <b>info@meshincentre.com</b>',
              icon: 'error',
              confirmButtonColor: '#f37b7b',
            });
            console.error('error', error);
          }
        );
      }
    });
  }

  onDeleteProduct(product: MedicalTest | Drug) {
    const isDrug = 'country_of_origin' in product;
    const productName = isDrug ? (product as Drug).name : (product as MedicalTest).name;
    const confirmText = `Are you sure you want to delete the ${productName} ${isDrug ? 'drug' : 'medical test'}?`;
    const deletedText = `${isDrug ? 'Drug' : 'Medical Test'} deleted!`;
    const linkedTitle = `${isDrug ? 'Drug' : 'Medical Test'} Linked to Orders`;
  
    if (!isDrug && this.medicalTestsWithOrders) {
      const isLinkedToOrders = this.medicalTestsWithOrders.some(test => test.id === product.id);
      if (isLinkedToOrders) {
        Swal.fire({
          title: linkedTitle,
          text: 'Deleting it may affect these orders.',
          icon: 'warning',
          confirmButtonText: 'Ok',
          confirmButtonColor: 'var(--thm-base)'
        });
        return;
      }
    } 
    Swal.fire({
      title: '',
      text: confirmText,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonColor: '#f37b7b',
      confirmButtonColor: 'var(--thm-base)'
    }).then((result) => {
      if (result.isConfirmed) {
        const deleteObservable = isDrug
          ? this.pharmacyService.deleteDrug(product.id)
          : this.medicalTestService.deleteMedicalTest(product.id);
  
        deleteObservable.pipe(first()).subscribe(
          () => {
            Swal.fire({
              title: '',
              text: deletedText,
              icon: 'success',
              confirmButtonText: 'Ok',
              confirmButtonColor: 'var(--thm-base)'
            }).then(() => {
              isDrug ? this.getAllDrugs() : this.getAllLocalProducts();
            });
          },
          error => {
            Swal.fire({
              title: 'An error occurred',
              html: 'Please contact us at: <b>info@meshincentre.com</b>',
              icon: 'error',
              confirmButtonColor: '#f37b7b'
            });
            console.error('error', error);
          }
        );
      }
    });
  }
  
  onDeleteOrder(order: Order) {
    Swal.fire({
      title: '',
      text: `Are you sure you want to delete order number ${order.order_number}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonColor: '#f37b7b',
      confirmButtonColor: 'var(--thm-base)',
    }).then((result) => {
      if (result.isConfirmed) {
        this.orderService.deleteOrder(order.id as number).pipe(first()).subscribe(
          order => {
          this.isMedicalTests = false;
          Swal.fire({
            title: '',
            text: 'Order deleted!',
            icon: 'success',
            confirmButtonText: 'Ok',
            confirmButtonColor: 'var(--thm-base)',
          }).then(() => this.getAllOrders())
        }, error => {
          Swal.fire({
            title: 'An error occurred',
            html: 'Please contact us at: <b>info@meshincentre.com</b>',
            icon: 'error',
            confirmButtonColor: '#f37b7b',
          });
          console.error('error', error);
        });
      }
    });
  }

  editRole(user: User) {
    const swal = Swal.fire({
      title: `Edit Role`,
      html: `<select
             id="roleSelect"
             class="swal2-input"
             placeholder="Select Role"
            >
              <option value="">--Select Role--</option>
              <option value="1">Admin</option>
              <option value="2">Admin1.1</option>
              <option value="3">Admin1.2</option>
              <option value="4">Admin1.3</option>
            </select>`,
      showCancelButton: true,
      confirmButtonText: 'Submit',
      confirmButtonColor: 'var(--thm-base)',
      focusConfirm: false,
      customClass: 'results-alert open',
      showLoaderOnConfirm: true,
      preConfirm: () => {
        const roleSelectElement = Swal.getPopup()?.querySelector('#roleSelect') as HTMLSelectElement | null;
        const roleId = roleSelectElement ? roleSelectElement.value : null;
        if (roleId) {
          return roleId as string;
        } else {
          Swal.showValidationMessage('Please select a role');
          return null;
        }
      }
    }).then(result => {
      if (result.isConfirmed) {
        user.role_id = parseInt(result.value as string);
        this.userProfileService.editUser(user, user.id).pipe(first()).subscribe(() => {
          Swal.fire({
            title: 'Updated!',
            text: 'Role has been updated.',
            icon: 'success',
            confirmButtonColor: 'var(--thm-base)' 
          });
          this.getAllAdmins(); 
        }, error => {
          Swal.fire('Error!', 'Role update failed.');
        });
      }
    });
  }

  loadRoles() {
    this.roleService.getRoles().subscribe(roles => {
      this.roles = roles as Role[];
      this.roles.forEach(role => {
        this.roleLabels[role.name] = this.roleOptions.find(option => option.value === role.name)?.label || 'Unknown Role';
        this.rolePermissions[role.name] = role.permissions?.map(p => p.name).join('<br>') || 'Unknown Permissions';
      });
    });
  }
}
